// Per-clinic marketing tracking for the public booking page.
// Each clinic sets its own GA4 / GTM / Meta Pixel / Google Ads IDs in Business info;
// we inject the vendor snippets at runtime and fire a conversion on a confirmed booking.
let inited = false;

const addScript = (src) => {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
  return s;
};

export function initTracking(biz) {
  if (inited || !biz) return;
  const { ga4_id, gtm_id, meta_pixel_id, google_ads_id } = biz;
  if (!ga4_id && !gtm_id && !meta_pixel_id && !google_ads_id) return;
  inited = true;

  // Google gtag (GA4 + Google Ads)
  if (ga4_id || google_ads_id) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    addScript(`https://www.googletagmanager.com/gtag/js?id=${ga4_id || google_ads_id}`);
    window.gtag("js", new Date());
    if (ga4_id) window.gtag("config", ga4_id);
    if (google_ads_id) window.gtag("config", google_ads_id);
  }

  // Google Tag Manager
  if (gtm_id) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    addScript(`https://www.googletagmanager.com/gtm.js?id=${gtm_id}`);
  }

  // Meta (Facebook) Pixel
  if (meta_pixel_id) {
    if (!window.fbq) {
      const n = (window.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      });
      if (!window._fbq) window._fbq = n;
      n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
      addScript("https://connect.facebook.net/en_US/fbevents.js");
    }
    window.fbq("init", meta_pixel_id);
    window.fbq("track", "PageView");
  }
}

// metaEvents = { Schedule: eventId, Lead?: eventId } from the server so Pixel + Conversions API dedupe.
export function trackBooking(biz, value, currency, metaEvents = {}) {
  if (!biz) return;
  const val = Number(value) || 0;
  const cur = currency || "AUD";
  if (window.gtag && biz.ga4_id) window.gtag("event", "booking", { currency: cur, value: val });
  if (window.gtag && biz.google_ads_id) window.gtag("event", "conversion", { send_to: biz.google_ads_id, value: val, currency: cur });
  if (window.fbq && biz.meta_pixel_id) {
    window.fbq("track", "Schedule", { value: val, currency: cur }, metaEvents.Schedule ? { eventID: metaEvents.Schedule } : undefined);
    if (metaEvents.Lead) window.fbq("track", "Lead", { value: val, currency: cur }, { eventID: metaEvents.Lead });
    // Neutral custom event: Meta blocks Schedule/Lead for "health & wellness" data sources; this one gets through and can back a Custom Conversion.
    const custom = customEventName(biz);
    if (custom) window.fbq("trackCustom", custom, { value: val, currency: cur }, metaEvents[custom] ? { eventID: metaEvents[custom] } : undefined);
  }
  if (window.dataLayer && biz.gtm_id) window.dataLayer.push({ event: "booking_confirmed", value: val, currency: cur });
  // Embedded in the clinic's own website: let the parent page's Pixel fire the same event (same eventID → Meta counts it once).
  try { if (window.parent && window.parent !== window) window.parent.postMessage({ type: "azordi:booking", value: val, currency: cur, meta_events: metaEvents, custom_event: customEventName(biz) || "" }, "*"); } catch { /* cross-origin parent unavailable */ }
}

// Deposit paid online → Meta `Purchase` (value = deposit). Same event IDs as the server's Conversions API call.
export function trackDepositPaid(biz, bookingId, amount, currency) {
  if (!biz || !bookingId) return;
  const val = Number(amount || 0), cur = currency || "AUD";
  const custom = customEventName(biz);
  const ids = { Purchase: `bk_${bookingId}_purchase`, ...(custom ? { [`${custom}_paid`]: `bk_${bookingId}_custom_paid` } : {}) };
  if (window.fbq && biz.meta_pixel_id) {
    window.fbq("track", "Purchase", { value: val, currency: cur }, { eventID: ids.Purchase });
    if (custom) window.fbq("trackCustom", `${custom}_paid`, { value: val, currency: cur }, { eventID: ids[`${custom}_paid`] });
  }
  if (window.gtag && biz.ga4_id) window.gtag("event", "purchase", { value: val, currency: cur, transaction_id: bookingId });
  try { if (window.parent && window.parent !== window) window.parent.postMessage({ type: "azordi:deposit", value: val, currency: cur, meta_events: ids, custom_event: custom ? `${custom}_paid` : "" }, "*"); } catch { /* cross-origin parent unavailable */ }
}

const customEventName = (biz) => ((biz && biz.meta_custom_event) || "").replace(/[^A-Za-z0-9_]/g, "").slice(0, 50);

const cookie = (name) => (document.cookie.split("; ").find((x) => x.startsWith(`${name}=`)) || "").split("=").slice(1).join("=");

// Which Meta app opened this page? Their in-app browsers announce themselves in the user agent; link-shim referrers back it up.
export function detectMetaApp() {
  try {
    const ua = navigator.userAgent || "";
    const ref = (document.referrer || "").toLowerCase();
    if (/FB_IAB\/MESSENGER|MessengerForiOS|MessengerLite|Orca-Android/i.test(ua) || ref.includes("l.messenger.com")) return "messenger";
    if (/Instagram/i.test(ua) || ref.includes("l.instagram.com")) return "instagram";
    if (/FBAN|FBAV|FB_IAB|FB4A/i.test(ua) || /l\.facebook\.com|lm\.facebook\.com/.test(ref)) return "facebook";
    return "";
  } catch { return ""; }
}

// Remember the ad click that brought this visitor (per clinic, for the session) so the booking can be attributed.
export function captureAttribution(slug) {
  try {
    const key = `pc_attr_${slug}`;
    const q = new URLSearchParams(window.location.search);
    const found = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid"].forEach((k) => { const v = q.get(k); if (v) found[k] = v; });
    // The link was opened inside Messenger / Instagram / Facebook — the app's browser identifies itself, so no tag is needed.
    const via = detectMetaApp();
    if (via && !sessionStorage.getItem(key)) {
      found.via = via;
      if (!found.utm_source) {
        found.utm_source = via === "instagram" ? "instagram" : "facebook";
        found.utm_medium = via === "messenger" ? "messenger" : `${via}_app`;
        found.auto = "true";
      }
    }
    if (Object.keys(found).length) {
      found.landing = window.location.href.slice(0, 200);
      if (found.fbclid && !cookie("_fbc")) document.cookie = `_fbc=fb.1.${Date.now()}.${encodeURIComponent(found.fbclid)}; Max-Age=7776000; Path=/; SameSite=Lax`;
      sessionStorage.setItem(key, JSON.stringify(found));
    }
    return JSON.parse(sessionStorage.getItem(key) || "{}");
  } catch { return {}; }
}

export const metaBrowserIds = () => ({ fbp: cookie("_fbp"), fbc: cookie("_fbc") });


// Where did this visitor come from? utm_source wins, else the referrer host, else direct.
export function detectTrafficSource() {
  try {
    const utm = (new URLSearchParams(window.location.search).get("utm_source") || "").toLowerCase();
    if (utm) return utm;
    const ref = document.referrer || "";
    if (!ref) return "direct";
    const host = new URL(ref).hostname.replace(/^www\./, "").toLowerCase();
    if (host === window.location.hostname.replace(/^www\./, "").toLowerCase()) return "direct";
    if (host.includes("instagram")) return "instagram";
    if (host.includes("facebook") || host.includes("fb.")) return "facebook";
    if (host.includes("google")) return "google";
    if (host.includes("tiktok")) return "tiktok";
    if (host.includes("bing")) return "bing";
    return "other";
  } catch { return "direct"; }
}
